/* RESPONSIBLE TEAM: team-automation-foundations */
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import { EntityType } from 'embercom/models/data/entity-types';
import safeWindowOpen from 'embercom/lib/safe-window-open';

/**
 * Parent route for /ask on standalone workspaces.
 * Mirrors apps.app.automation.fin-ai-agent.ask but without the automation parent route's
 * permission guards and messenger settings loading.
 */
export default class AskRoute extends Route {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare router: RouterService;

  private unsubscribeKnowledgeHub?: () => void;
  private unsubscribeNavigate?: () => void;

  activate() {
    this.unsubscribeKnowledgeHub = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'knowledge-hub:open-content',
      (data: Record<string, unknown>) => {
        let contentId = data.contentId as number | string;
        let contentType = data.contentType as string;
        let editorMode = (data.editorMode as string) || 'view';

        if (editorMode === 'view') {
          this.knowledgeHubDrawerEditorService.openViewDrawer({
            activeContentId: contentId,
            activeContentType: contentType,
            newTab: false,
          });
        } else {
          this.knowledgeHubDrawerEditorService.openEditDrawer({
            activeContentId: contentId,
            activeContentType: contentType,
            newTab: false,
          });
        }
      },
    );

    this.unsubscribeNavigate = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'internal-agent:navigate',
      (data: Record<string, unknown>) => {
        let entityType = data.entityType as number;
        let entityId = data.entityId as number;
        let stepId = data.stepId as string | undefined;

        switch (entityType) {
          case EntityType.Guideline:
            this.router.transitionTo('apps.app.standalone.guidance');
            break;
          case EntityType.Procedure:
            this.router.transitionTo('apps.app.standalone.tasks.task', entityId);
            break;
          case EntityType.Conversation:
            safeWindowOpen(
              this.router.urlFor('apps.app.inbox.inbox.conversation', entityId),
              '_blank',
            );
            break;
          case EntityType.Workflow:
          case EntityType.TriggerableCustomBot:
            this.router.transitionTo('apps.app.automation.workflows.edit', entityId, {
              queryParams: { visual: 'enabled', step_id: stepId ?? null },
            });
            break;
        }
      },
    );
  }

  deactivate() {
    if (this.unsubscribeKnowledgeHub) {
      this.unsubscribeKnowledgeHub();
      this.unsubscribeKnowledgeHub = undefined;
    }
    if (this.unsubscribeNavigate) {
      this.unsubscribeNavigate();
      this.unsubscribeNavigate = undefined;
    }
  }
}
